import { ACCOUNT_SECURITY_CAPABILITIES, REPORTING_ROUTE_IDS, ROUTE_IDS } from '@finance/contracts';
import type { RouteId } from '@finance/contracts';
import type { BespokePageEntry } from './bespoke-allowlist';
import { BESPOKE_PAGE_ALLOWLIST } from './bespoke-allowlist';

const REPORT_DETAIL_PAGE_ENTRY: BespokePageEntry = {
  routeId: ROUTE_IDS.REPORT_DETAIL,
  component: () =>
    import('../../routes/reports/ReportDetailPage').then((module) => ({
      default: module.ReportDetailPage,
    })),
  owner: 'investment-reporting-ux',
  reason: 'Durable report-detail URL for live reports including client statements',
  regulated: true,
  migrationTarget: 'page.reports.detail.generic',
  reviewDate: '2027-Q1',
};

const REPORTS_PAGE_ENTRY: BespokePageEntry = {
  routeId: ROUTE_IDS.REPORTS,
  component: () => import('../../routes/reports/ReportsRoutePage').then((module) => ({ default: module.ReportsRoutePage })),
  owner: 'investment-reporting-ux',
  reason: 'Persona-aware report library backed by the authorized report catalog',
  regulated: true,
  migrationTarget: 'page.reports.library.generic',
  reviewDate: '2027-Q1',
};

const REPORTS_LIVE_ENTRY: BespokePageEntry = {
  routeId: REPORTING_ROUTE_IDS.LIVE,
  component: () => import('../../routes/reports/LiveReportsPage').then((module) => ({ default: module.LiveReportsPage })),
  owner: 'investment-reporting-ux',
  reason: 'Essential latest authoritative financial report previews backed by canonical report execution',
  regulated: true,
  migrationTarget: 'page.reports.live.generic',
  reviewDate: '2027-Q1',
};

const REPORTS_REGULATORY_ENTRY: BespokePageEntry = {
  routeId: REPORTING_ROUTE_IDS.REGULATORY,
  component: () => import('../../routes/reports/ReportsRegulatoryPage').then((module) => ({ default: module.ReportsRegulatoryPage })),
  owner: 'investment-reporting-ux',
  reason: 'Regulated definition, generation, validation, submission and artifact lifecycle workspace',
  regulated: true,
  migrationTarget: 'page.reports.regulatory.generic',
  reviewDate: '2027-Q1',
};

const REPORTS_OPERATIONS_ENTRY: BespokePageEntry = {
  routeId: REPORTING_ROUTE_IDS.OPERATIONS,
  component: () => import('../../routes/reports/ReportOperationsPages').then((module) => ({ default: module.ReportsOperationsPage })),
  owner: 'investment-reporting-ux',
  reason: 'Authorized landing page for valuation and financial-control workflows',
  regulated: true,
  migrationTarget: 'page.reports.operations.generic',
  reviewDate: '2027-Q1',
};

const REPORTS_FIXED_INCOME_ENTRY: BespokePageEntry = {
  routeId: REPORTING_ROUTE_IDS.OPERATIONS_FIXED_INCOME,
  component: () => import('../../routes/reports/ReportOperationsPages').then((module) => ({ default: module.FixedIncomeOperationsPage })),
  owner: 'investment-reporting-ux',
  reason: 'Dedicated fixed-income valuation and event lifecycle workspace',
  regulated: true,
  migrationTarget: 'page.reports.operations.fixed-income.generic',
  reviewDate: '2027-Q1',
};

const REPORTS_VALUATION_RUNS_ENTRY: BespokePageEntry = {
  routeId: REPORTING_ROUTE_IDS.OPERATIONS_VALUATION_RUNS,
  component: () => import('../../routes/reports/ReportOperationsPages').then((module) => ({ default: module.ValuationRunsOperationsPage })),
  owner: 'investment-reporting-ux',
  reason: 'Dedicated governed portfolio valuation run lifecycle workspace',
  regulated: true,
  migrationTarget: 'page.reports.operations.valuation-runs.generic',
  reviewDate: '2027-Q1',
};

const REPORTS_CUTOFF_CONTROLS_ENTRY: BespokePageEntry = {
  routeId: REPORTING_ROUTE_IDS.OPERATIONS_CUTOFF_CONTROLS,
  component: () => import('../../routes/reports/ReportOperationsPages').then((module) => ({ default: module.CutoffControlsOperationsPage })),
  owner: 'investment-reporting-ux',
  reason: 'Dedicated cutoff-control results and evidence workspace',
  regulated: true,
  migrationTarget: 'page.reports.operations.cutoff-controls.generic',
  reviewDate: '2027-Q1',
};

const ORGANIZATION_REGULATORY_PROFILE_ENTRY: BespokePageEntry = {
  routeId: ROUTE_IDS.ORGANIZATION_REGULATORY_PROFILE,
  component: () =>
    import('../../routes/admin/OrganizationRegulatoryProfilePage').then((module) => ({
      default: module.OrganizationRegulatoryProfilePage,
    })),
  owner: 'organization-administration',
  reason: 'Thin route adapter supplies the active organization identifier to a backend-owned generic resource form',
  regulated: true,
  migrationTarget: 'resource.organization-regulatory-profile.generic-singleton',
  reviewDate: '2027-Q1',
};

const ORGANIZATION_JURISDICTION_SETTINGS_ENTRY: BespokePageEntry = {
  routeId: ROUTE_IDS.ORGANIZATION_JURISDICTION_SETTINGS,
  component: () =>
    import('../../routes/admin/JurisdictionSettingsPage').then((module) => ({
      default: module.JurisdictionSettingsPage,
    })),
  owner: 'organization-administration',
  reason: 'V1 stop-gap UI for one-exchange governed trading-calendar setup; backend remains the calendar/version authority',
  regulated: true,
  migrationTarget: 'resource.organization-jurisdiction-settings.generic',
  reviewDate: '2027-Q1',
};

const IMPORT_WIZARD_ENTRY: BespokePageEntry = {
  routeId: ROUTE_IDS.DATA_IMPORT_UPLOAD,
  component: () =>
    import('../../routes/import/ImportWizardPage').then((module) => ({
      default: module.ImportWizardPage,
    })),
  owner: 'investment-data-ux',
  reason: 'Multi-step import wizard (Upload → Map → Validate → Preview → Commit → Results) with file parsing, column mapping, and server-side validation/commit lifecycle',
  regulated: true,
  migrationTarget: 'page.data.import-wizard.generic',
  reviewDate: '2027-Q1',
};

const OPENING_CUTOVER_HISTORY_ENTRY: BespokePageEntry = {
  routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_HISTORY,
  component: () =>
    import('../../routes/admin/OpeningCutoverHistoryPage').then((module) => ({
      default: module.OpeningCutoverHistoryPage,
    })),
  owner: 'organization-administration',
  reason: 'Read-only lifecycle-history view of the canonical Opening Cutover aggregate',
  regulated: true,
  migrationTarget: 'resource.organization-opening-cutover.history-view',
  reviewDate: '2027-Q1',
};

const GENERIC_EXPERIENCE_ROUTES = new Set<RouteId>([
  ROUTE_IDS.ORGANIZATION_USERS_LIST,
  ROUTE_IDS.ORGANIZATION_USERS_CREATE,
  ROUTE_IDS.ORGANIZATION_USERS_EDIT,
  ROUTE_IDS.ORGANIZATION_ROLES_LIST,
  ROUTE_IDS.ORGANIZATION_ROLES_CREATE,
  ROUTE_IDS.ORGANIZATION_ROLES_EDIT,
]);

const UNAVAILABLE_BESPOKE_ROUTES = new Set<RouteId>(
  ACCOUNT_SECURITY_CAPABILITIES.mfaManagement ? [] : [ROUTE_IDS.SETTINGS_MFA],
);

/** Effective bespoke renderer authority consumed by runtime and parity tests. */
export const EFFECTIVE_BESPOKE_PAGE_REGISTRY: BespokePageEntry[] = [
  ...BESPOKE_PAGE_ALLOWLIST.filter(
    (entry) =>
      !GENERIC_EXPERIENCE_ROUTES.has(entry.routeId)
      && !UNAVAILABLE_BESPOKE_ROUTES.has(entry.routeId),
  ),
  REPORTS_PAGE_ENTRY,
  REPORT_DETAIL_PAGE_ENTRY,
  REPORTS_LIVE_ENTRY,
  REPORTS_REGULATORY_ENTRY,
  REPORTS_OPERATIONS_ENTRY,
  REPORTS_FIXED_INCOME_ENTRY,
  REPORTS_VALUATION_RUNS_ENTRY,
  REPORTS_CUTOFF_CONTROLS_ENTRY,
  ORGANIZATION_REGULATORY_PROFILE_ENTRY,
  ORGANIZATION_JURISDICTION_SETTINGS_ENTRY,
  IMPORT_WIZARD_ENTRY,
  OPENING_CUTOVER_HISTORY_ENTRY,
];

export function isBespokeRoute(routeId: string): boolean {
  return EFFECTIVE_BESPOKE_PAGE_REGISTRY.some((entry) => entry.routeId === routeId);
}

export function getBespokeEntry(routeId: string): BespokePageEntry | undefined {
  return EFFECTIVE_BESPOKE_PAGE_REGISTRY.find((entry) => entry.routeId === routeId);
}
